/**
 * Ungated render functions — visible to all visitors before email gate.
 */

import { esc, fmtMin, fmtTimeHtml, fmtCompact, medianOf } from './shared.js';

// ============================================================
// Section 1: The ChatGPT Tax (Hero)
// ============================================================

export function renderChatGPTTax(result) {
  const { taxPaid, projected, archiveSpanDays } = result.chatgptTax;

  const total = taxPaid.totalMinutes || 1;
  const waitingPct = (taxPaid.waitingLossMinutes / total * 100).toFixed(1);
  const cognitivePct = (taxPaid.cognitiveLossMinutes / total * 100).toFixed(1);
  const wastePct = ((taxPaid.contextWasteMinutes || 0) / total * 100).toFixed(1);

  // Waiting panel stats
  const convsWithRender = result.conversations.filter(c =>
    c.turnPairs.some(tp => tp.perceivedWait)
  ).length;
  const perConvMedians = result.conversations
    .map(c => {
      const waits = c.turnPairs
        .filter(tp => tp.perceivedWait)
        .map(tp => tp.perceivedWait.total);
      return waits.length > 0 ? medianOf([...waits].sort((a, b) => a - b)) : null;
    })
    .filter(m => m !== null)
    .sort((a, b) => a - b);
  const archiveMedianWait = perConvMedians.length > 0
    ? medianOf(perConvMedians)
    : null;
  const medianWaitStr = archiveMedianWait !== null
    ? `${archiveMedianWait.toFixed(1)}s`
    : '\u2014';

  // Context waste panel
  const wasteEmpty = !taxPaid.contextWasteCount;

  return `
    <section class="section" id="section-tax">
      <div class="section-label">The ChatGPT Tax</div>

      <div class="metric-hero">
        <div class="metric-value" data-counter="${taxPaid.totalHours}">0</div>
        <div class="metric-label">hours lost to ChatGPT inefficiency</div>
        <div class="metric-detail">over ${Math.round(archiveSpanDays)} days of usage</div>
      </div>

      <div class="tax-bar-wrap">
        <div class="tax-bar">
          <div class="tax-bar-seg" data-tax-type="waiting" style="width:${waitingPct}%;background:var(--denim)">${parseFloat(waitingPct) > 8 ? waitingPct + '%' : ''}</div>
          <div class="tax-bar-seg" data-tax-type="cognitive" style="width:${cognitivePct}%;background:var(--accent)">${parseFloat(cognitivePct) > 8 ? cognitivePct + '%' : ''}</div>
          <div class="tax-bar-seg" data-tax-type="waste" style="width:${wastePct}%;background:var(--red)">${parseFloat(wastePct) > 8 ? wastePct + '%' : ''}</div>
        </div>
        <div class="tax-legend">
          <div class="tax-legend-item" data-tax-type="waiting">
            <span class="dot" style="background:var(--denim)"></span>
            The Drag: ${fmtMin(taxPaid.waitingLossMinutes)}
          </div>
          <div class="tax-legend-item" data-tax-type="cognitive">
            <span class="dot" style="background:var(--accent)"></span>
            Stretch Marks: ${fmtMin(taxPaid.cognitiveLossMinutes)}
          </div>
          <div class="tax-legend-item" data-tax-type="waste">
            <span class="dot" style="background:var(--red)"></span>
            Spare Tire: ${wasteEmpty ? '\u2014' : fmtMin(taxPaid.contextWasteMinutes)}
          </div>
        </div>

        <div class="tax-detail-panel" data-tax-panel="waiting" hidden>
          <p class="tax-explain">Time spent waiting for responses to appear in your browser. As conversations grow, each new response takes longer to render &mdash; not because the model is thinking longer, but because your browser is redrawing more and more content each time.</p>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">Total waiting</div>
              <div class="metric-value">${fmtTimeHtml(taxPaid.waitingLossMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Chats with render data</div>
              <div class="metric-value">${convsWithRender}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Median delay per response</div>
              <div class="metric-value">${medianWaitStr}</div>
            </div>
          </div>
          <div class="tax-contributors">
            <div class="section-label">What contributes</div>
            <ul>
              <li>Long conversations with high message counts</li>
              <li>Code-heavy responses (large DOM elements)</li>
              <li>Conversations with many tool call outputs</li>
            </ul>
          </div>
        </div>

        <div class="tax-detail-panel" data-tax-panel="cognitive" hidden>
          <p class="tax-explain">Time <em>you</em> spend compensating when ChatGPT's memory fades. As a conversation grows, the model's grip on earlier context weakens &mdash; so you repeat instructions it should already know, or give up and start a fresh chat on the same topic, re-establishing context from scratch each time.</p>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">Re-instructions</div>
              <div class="metric-value">${taxPaid.reinstructionCount}<span class="unit">instances</span></div>
              <div class="metric-detail">${fmtMin(taxPaid.reinstructionMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Topic restarts</div>
              <div class="metric-value">${taxPaid.topicRestartExtraConversations}<span class="unit">extra chats</span></div>
              <div class="metric-detail">${fmtMin(taxPaid.topicRestartMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Combined</div>
              <div class="metric-value">${fmtTimeHtml(taxPaid.cognitiveLossMinutes)}</div>
            </div>
          </div>
          <div class="tax-contributors">
            <div class="section-label">What contributes</div>
            <ul>
              <li>Long conversations where the model loses track of earlier instructions</li>
              <li>Not using Custom Instructions for recurring context</li>
              <li>Starting new chats instead of continuing existing ones or using Projects</li>
            </ul>
          </div>
        </div>

        <div class="tax-detail-panel" data-tax-panel="waste" hidden>
          ${wasteEmpty ? `
          <p class="tax-explain">No messages landed in the ghost zone. Your conversations stayed within the model's context window.</p>
          ` : `
          <p class="tax-explain">Messages you sent into the <strong>ghost zone</strong> &mdash; the point in a conversation where it has exceeded the context window and ChatGPT literally cannot see your earlier messages anymore. Unlike Stretch Marks (where the model is struggling but still trying), these messages were never processed at all. ChatGPT keeps responding as if everything is fine, so you don't realize it's lost the thread.</p>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">Ghost zone messages</div>
              <div class="metric-value">${taxPaid.contextWasteCount}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Time lost</div>
              <div class="metric-value">${fmtTimeHtml(taxPaid.contextWasteMinutes)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Chats in the ghost zone</div>
              <div class="metric-value">${result.ghostZoneConversations}</div>
            </div>
          </div>
          <div class="tax-contributors">
            <div class="section-label">What contributes</div>
            <ul>
              <li>Very long conversations that blow past the context window</li>
              <li>Hidden token consumers: tool outputs, system prompts, and images eating context you can't see</li>
              <li>Continuing to add to chats well past the overflow point</li>
            </ul>
          </div>
          `}
        </div>
      </div>

      <div class="section-label" style="margin-top:2.5rem">At your current pace</div>
      <div class="metric-grid">
        <div class="metric-card">
          <div class="metric-label">This week</div>
          <div class="metric-value">${fmtTimeHtml(projected.weekMinutes)}</div>
          <div class="metric-detail">${projected.dailyRate.toFixed(1)} min/day</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">This month</div>
          <div class="metric-value">${fmtTimeHtml(projected.monthMinutes)}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">This year</div>
          <div class="metric-value">${fmtTimeHtml(projected.yearMinutes)}</div>
        </div>
      </div>

      <div class="metric-detail" style="margin-top:0.75rem;text-align:center">
        Based on last ${projected.recentWindowDays} days:
        ${fmtMin(projected.recentRenderMinutes)} drag +
        ${fmtMin(projected.recentReinstructionMinutes + projected.recentRestartMinutes)} stretch marks +
        ${fmtMin(projected.recentContextWasteMinutes || 0)} spare tire
      </div>
    </section>
  `;
}

// ============================================================
// Tax Breakdown Accordion
// ============================================================

export function initTaxBreakdown() {
  const wrap = document.querySelector('.tax-bar-wrap');
  if (!wrap) return;

  wrap.addEventListener('click', (e) => {
    const target = e.target.closest('[data-tax-type]');
    if (!target) return;
    const type = target.dataset.taxType;
    toggleTaxPanel(wrap, type);
  });
}

function toggleTaxPanel(wrap, type) {
  const panel = wrap.querySelector(`[data-tax-panel="${type}"]`);
  if (!panel) return;

  const isOpen = !panel.hidden;

  // Close all panels and remove active states
  wrap.querySelectorAll('.tax-detail-panel').forEach(p => p.hidden = true);
  wrap.querySelectorAll('.tax-bar-seg').forEach(s => s.classList.remove('active'));
  wrap.querySelectorAll('.tax-legend-item').forEach(l => l.classList.remove('active'));

  // If it was closed, open it
  if (!isOpen) {
    panel.hidden = false;
    wrap.querySelectorAll(`[data-tax-type="${type}"]`).forEach(el => el.classList.add('active'));
  }
}

// ============================================================
// Section 2: The Weigh-In (Bloat Check)
// ============================================================

export function renderBloatCheck(result) {
  const total = result.totalConversations || 1;
  const ghostPct = ((result.ghostZoneConversations / total) * 100).toFixed(0);

  // Jeans score thresholds — Blown Out is anything above 1.0
  const wash = [0, 0, 0, 0, 0];
  for (const c of result.conversations) {
    const s = c.jeansScore ? c.jeansScore.score : 0;
    if (s <= 0.35) wash[0]++;
    else if (s <= 0.55) wash[1]++;
    else if (s <= 0.75) wash[2]++;
    else if (s <= 1.0) wash[3]++;
    else wash[4]++;
  }

  const washPct = wash.map(n => (n / total * 100).toFixed(1));
  const washNames = ['Loose Fit', 'Snug', 'Straining', 'Splitting', 'Blown Out'];
  const washKeys = ['loose-fit', 'snug', 'straining', 'splitting', 'blown-out'];
  const washRanges = ['0\u20130.35', '0.35\u20130.55', '0.55\u20130.75', '0.75\u20131.0', '1.0+'];
  const washDescs = [
    'Plenty of room. Next message will render fast, low risk of re-instruction or overflow.',
    'Starting to feel it. Some render lag or mild cognitive overhead on the next turn.',
    'Starting to feel painful. Noticeable wait on responses, re-instruction creeping in, or context getting tight.',
    'Clearly noticeable. Sluggish responses, repeated re-explaining, or context window under real pressure.',
    'Painful. Heavy render delay, high re-instruction cost, and context overflowed \u2014 this chat needs a fresh start.',
  ];

  const fatCount = wash[2] + wash[3] + wash[4];
  const fatStartPct = parseFloat(washPct[0]) + parseFloat(washPct[1]);

  return `
    <section class="section" id="section-bloat">
      <div class="section-label">The Weigh-In</div>
      <div class="section-title">${fatCount} of ${result.totalConversations} chats are fat</div>
      <div class="section-desc">
        How your next message will feel. Combines render drag, re-instruction risk,
        and context overflow into a single fit score.
      </div>

      <div class="bloat-bar-wrap" style="position:relative">
        <div class="bloat-bar">
          ${wash.map((n, i) => `<div class="bloat-seg wash-${i + 1}" data-wash-type="${washKeys[i]}" style="width:${washPct[i]}%">${n > 0 ? n : ''}</div>`).join('')}
        </div>
        ${fatCount > 0 ? `<div class="bloat-bar-fat-marker" style="left:${fatStartPct.toFixed(1)}%">
          <div class="bloat-bar-fat-line"></div>
          <div class="bloat-bar-fat-label">${fatCount} fat \u2192</div>
        </div>` : ''}
        <div class="bloat-legend">
          ${wash.map((n, i) => `<span data-wash-type="${washKeys[i]}"><span class="legend-dot wash-${i + 1}"></span> ${washNames[i]} (${n})</span>`).join('')}
        </div>

        ${washKeys.map((key, i) => `<div class="wash-detail-panel" data-wash-panel="${key}" style="display:none"><p class="wash-explain"><strong>${washNames[i]}</strong> (${washRanges[i]}) \u2014 ${washDescs[i]}</p></div>`).join('')}
      </div>

      <div class="stats-row">
        <div>
          <span class="stat-value">${result.ghostZoneConversations}</span>
          <span class="stat-label">Ghost zone chats</span>
        </div>
        <div>
          <span class="stat-value">${ghostPct}%</span>
          <span class="stat-label">of all chats</span>
        </div>
      </div>
    </section>
  `;
}

// ============================================================
// Wash Breakdown Accordion (Weigh-In)
// ============================================================

export function initWashBreakdown() {
  const wrap = document.querySelector('.bloat-bar-wrap');
  if (!wrap) return;

  // Attach click directly to each clickable element
  wrap.querySelectorAll('[data-wash-type]').forEach(el => {
    el.addEventListener('click', () => {
      toggleWashPanel(wrap, el.dataset.washType);
    });
  });
}

function toggleWashPanel(wrap, type) {
  const panel = wrap.querySelector(`[data-wash-panel="${type}"]`);
  if (!panel) return;

  const isOpen = panel.style.display !== 'none';

  // Close all panels and remove active states
  wrap.querySelectorAll('.wash-detail-panel').forEach(p => p.style.display = 'none');
  wrap.querySelectorAll('.bloat-seg').forEach(s => s.classList.remove('active'));
  wrap.querySelectorAll('.bloat-legend span[data-wash-type]').forEach(l => l.classList.remove('active'));

  // If it was closed, open it
  if (!isOpen) {
    panel.style.display = 'block';
    wrap.querySelectorAll(`[data-wash-type="${type}"]`).forEach(el => el.classList.add('active'));
  }
}

// ============================================================
// Section 3: Jeans Card
// ============================================================

export function renderJeansCard(result) {
  const total = result.totalConversations || 1;
  const avgTokens = result.totalTokens / total;
  const avgMessages = result.totalMessages / total;
  const tokenSignal = Math.min(1, avgTokens / 70000);
  const messageSignal = Math.min(1, avgMessages / 80);
  const cardScore = tokenSignal * 0.6 + messageSignal * 0.4;
  const bloatPct = Math.round(cardScore * 100);
  const hours = result.chatgptTax.taxPaid.totalHours;

  const tier = getJeansTier(cardScore);

  return `
    <section class="section" id="section-jeans">
      <div class="section-label">Your Size Tag</div>

      <div class="jeans-card" id="jeans-card">
        <div class="jeans-card-image">
          <img src="${tier.img}" alt="${esc(tier.fit)}" />
        </div>
        <div class="jeans-card-content">
          <div class="jeans-brand">DO MY CHATS LOOK FAT?</div>
          <div class="jeans-hours">${bloatPct}<span class="unit">%</span></div>
          <div class="jeans-label">BLOATED</div>
          <div class="jeans-stats">
            ${result.totalConversations} chats &middot;
            ${result.totalMessages.toLocaleString()} messages &middot;
            ${hours.toFixed(1)} hrs lost
          </div>
          <hr class="jeans-fold">
          <div class="jeans-tier-label">${esc(tier.fit)}</div>
        </div>
        <div class="jeans-watermark">domychatslookfat.com</div>
      </div>

      <div class="jeans-actions">
        <button class="jeans-btn primary" id="btn-download-card">Download Share Card</button>
        <button class="jeans-btn" id="btn-challenge">Challenge a Friend</button>
      </div>
    </section>
  `;
}

function getJeansTier(cardScore) {
  if (cardScore < 0.15) return { tier: 1, fit: 'Loose Fit', img: 'img/tier-1-loose-fit-app.jpg' };
  if (cardScore < 0.35) return { tier: 2, fit: 'Snug', img: 'img/tier-2-snug-app.jpg' };
  if (cardScore < 0.55) return { tier: 3, fit: 'Straining', img: 'img/tier-3-straining-app.jpg' };
  if (cardScore < 0.80) return { tier: 4, fit: 'Splitting', img: 'img/tier-4-splitting-app.jpg' };
  return { tier: 5, fit: 'Blown Out', img: 'img/tier-5-blown-out-app.jpg' };
}

// ============================================================
// Section 4: X-Ray Summary (ungated headline only)
// ============================================================

export function renderXraySummary(result) {
  let totalTextTokens = 0, totalImageTokens = 0, totalToolTokens = 0, totalSystemTokens = 0;
  let totalCodeChars = 0, totalTableChars = 0;

  for (const c of result.conversations) {
    totalTextTokens += c.adjustedTokens.textTokens;
    totalImageTokens += c.adjustedTokens.imageTokens;
    totalToolTokens += c.adjustedTokens.toolCallTokens;
    totalSystemTokens += c.adjustedTokens.systemTokens;
    totalCodeChars += c.contentProfile.codeBlockChars;
    totalTableChars += c.contentProfile.tableChars;
  }

  const codeTokens = Math.ceil(totalCodeChars / 4);
  const tableTokens = Math.ceil(totalTableChars / 4);
  const plainTextTokens = Math.max(0, totalTextTokens - codeTokens - tableTokens);

  const grandTotal = totalTextTokens + totalImageTokens + totalToolTokens + totalSystemTokens;
  const invisibleTokens = totalToolTokens + totalSystemTokens;
  const invisiblePct = grandTotal > 0 ? (invisibleTokens / grandTotal * 100) : 0;

  const plainPct = grandTotal > 0 ? (plainTextTokens / grandTotal * 100) : 0;
  const codePct = grandTotal > 0 ? (codeTokens / grandTotal * 100) : 0;
  const tablePct = grandTotal > 0 ? (tableTokens / grandTotal * 100) : 0;
  const imagePct = grandTotal > 0 ? (totalImageTokens / grandTotal * 100) : 0;
  const toolPct = grandTotal > 0 ? (totalToolTokens / grandTotal * 100) : 0;
  const systemPct = grandTotal > 0 ? (totalSystemTokens / grandTotal * 100) : 0;

  return `
    <section class="section" id="section-xray">
      <div class="section-label">Context X-Ray</div>
      <div class="section-title">What's really eating your context</div>
      <div class="section-desc">
        Your conversations contain more than just your messages. Tool outputs, system prompts,
        and image tokens silently consume context.
      </div>

      ${invisiblePct > 0.5 ? `
      <div class="xray-callout">
        <div class="xray-callout-value">${invisiblePct.toFixed(1)}%</div>
        <div class="xray-callout-label">of your total context is invisible overhead</div>
        <div class="xray-callout-detail">${fmtCompact(invisibleTokens)} tokens you never see</div>
      </div>
      ` : ''}

      <div class="xray-bar-wrap">
        <div class="section-label" style="margin-bottom:0.5rem">Token composition across all chats</div>
        <div class="xray-bar">
          <div class="xray-seg" title="Plain text: ${plainPct.toFixed(1)}%" style="width:${plainPct.toFixed(1)}%;background:var(--text-secondary)"></div>
          ${codePct > 0.1 ? `<div class="xray-seg" title="Code: ${codePct.toFixed(1)}%" style="width:${codePct.toFixed(1)}%;background:var(--cyan)"></div>` : ''}
          ${tablePct > 0.1 ? `<div class="xray-seg" title="Tables: ${tablePct.toFixed(1)}%" style="width:${tablePct.toFixed(1)}%;background:var(--yellow)"></div>` : ''}
          ${imagePct > 0.1 ? `<div class="xray-seg" title="Images: ${imagePct.toFixed(1)}%" style="width:${imagePct.toFixed(1)}%;background:var(--purple)"></div>` : ''}
          ${toolPct > 0.1 ? `<div class="xray-seg invisible" title="Tool output: ${toolPct.toFixed(1)}%" style="width:${toolPct.toFixed(1)}%;background:var(--accent)"></div>` : ''}
          ${systemPct > 0.1 ? `<div class="xray-seg invisible" title="System: ${systemPct.toFixed(1)}%" style="width:${systemPct.toFixed(1)}%;background:var(--pink)"></div>` : ''}
        </div>
        <div class="xray-legend">
          <span><span class="legend-dot" style="background:var(--text-secondary)"></span> Plain text ${fmtCompact(plainTextTokens)}</span>
          ${codeTokens > 0 ? `<span><span class="legend-dot" style="background:var(--cyan)"></span> Code ${fmtCompact(codeTokens)}</span>` : ''}
          ${tableTokens > 0 ? `<span><span class="legend-dot" style="background:var(--yellow)"></span> Tables ${fmtCompact(tableTokens)}</span>` : ''}
          ${totalImageTokens > 0 ? `<span><span class="legend-dot" style="background:var(--purple)"></span> Images ${fmtCompact(totalImageTokens)}</span>` : ''}
          ${totalToolTokens > 0 ? `<span><span class="legend-dot" style="background:var(--accent)"></span> Tool output ${fmtCompact(totalToolTokens)}</span>` : ''}
          ${totalSystemTokens > 0 ? `<span><span class="legend-dot" style="background:var(--pink)"></span> System ${fmtCompact(totalSystemTokens)}</span>` : ''}
        </div>
      </div>
    </section>
  `;
}
